import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Save, UserCircle } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatDateID } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm_password: "" });
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { name, email });
      setUser((u) => ({ ...u, ...data }));
      toast.success("Profil berhasil diperbarui");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    setSavingPw(true);
    try {
      await api.put("/profile/password", pw);
      toast.success("Password berhasil diubah");
      setPw({ old_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="profile-page">
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-6 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-slate-900 text-white flex items-center justify-center text-2xl font-bold">
          {(user?.name || "A")[0].toUpperCase()}
        </div>
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-100">{user?.name}</h2>
          <p className="text-sm text-slate-400">@{user?.username} · {user?.email}</p>
          <div className="flex gap-2 mt-2">
            <span className="bg-slate-900 text-white rounded-full px-3 py-0.5 text-xs font-semibold">{user?.role_label}</span>
            <span className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-full px-3 py-0.5 text-xs font-semibold">
              {user?.status === "aktif" ? "Aktif" : "Nonaktif"}
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Login terakhir: {user?.last_login ? `${formatDateID(user.last_login.slice(0, 10))} ${user.last_login.slice(11, 16)}` : "-"}
          </p>
        </div>
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-6 space-y-4">
        <div className="flex items-center gap-2">
          <UserCircle className="w-4 h-4 text-slate-400" />
          <h3 className="text-base font-bold text-slate-100">Ubah Profil</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Nama Lengkap</Label>
            <Input data-testid="input-profile-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input data-testid="input-profile-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>
        <p className="text-xs text-slate-400">Role hanya dapat diubah oleh pengelola pengguna, bukan dari halaman ini.</p>
        <Button data-testid="btn-profile-save" onClick={saveProfile} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Simpan Profil
        </Button>
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl shadow-sm p-6 space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-slate-400" />
          <h3 className="text-base font-bold text-slate-100">Ubah Password</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label>Password Lama</Label>
            <Input data-testid="input-old-password" type="password" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Password Baru (min. 8)</Label>
            <Input data-testid="input-new-password" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Konfirmasi Password Baru</Label>
            <Input data-testid="input-confirm-password" type="password" value={pw.confirm_password} onChange={(e) => setPw({ ...pw, confirm_password: e.target.value })} />
          </div>
        </div>
        <Button data-testid="btn-password-save" onClick={savePassword} disabled={savingPw} className="bg-slate-900 hover:bg-slate-800 text-white">
          {savingPw && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Ubah Password
        </Button>
      </div>
    </div>
  );
}
